import { useState, useCallback, FormEvent, useEffect } from 'react';
import { Modal, Button, Input, Alert } from '@components/ui';
import { CreditCardIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { CardInfo, AvailablePlan } from '../types/subscription.types';
import { trackingHelpers } from '@/services/mixpanel';

import { validateLuhn, detectCardType, getExpectedCVVLength, validateExpiryDate } from '../utils/cardValidation';

// Bank decline codes - matches backend BANK_DECLINE_CODES
const BANK_DECLINE_MESSAGES: Record<string, { message: string; retryable: boolean }> = {
  '51': { message: 'Fondos insuficientes. Por favor, usa otra tarjeta.', retryable: false },
  '05': { message: 'Transacción no autorizada por el banco. Contacta a tu banco o usa otra tarjeta.', retryable: false },
  '14': { message: 'Número de tarjeta inválido. Verifica los datos e intenta de nuevo.', retryable: true },
  '54': { message: 'La tarjeta está vencida. Por favor, usa otra tarjeta.', retryable: false },
  '57': { message: 'Este tipo de transacción no está permitida para tu tarjeta.', retryable: false },
  '61': { message: 'El monto excede el límite de retiro de tu tarjeta.', retryable: false },
  '65': { message: 'Has excedido el número de transacciones permitidas. Intenta más tarde.', retryable: true },
  '91': { message: 'El emisor de la tarjeta no está disponible. Intenta más tarde.', retryable: true },
};

// General error messages
const ERROR_MESSAGES: Record<string, string> = {
  'card_declined': 'La tarjeta fue rechazada. Por favor, verifica los datos o usa otra tarjeta.',
  'insufficient_funds': 'Fondos insuficientes. Por favor, usa otra tarjeta.',
  'invalid_card': 'Los datos de la tarjeta son inválidos. Por favor, verifica e intenta de nuevo.',
  'expired_card': 'La tarjeta está vencida. Por favor, usa otra tarjeta.',
  'invalid_cvv': 'El código de seguridad (CVV) es incorrecto.',
  'processing_error': 'Error al procesar el pago. Por favor, intenta de nuevo.',
  'network_error': 'Error de conexión. Verifica tu internet e intenta de nuevo.',
  'default': 'Ocurrió un error al procesar el pago. Por favor, intenta de nuevo.',
};

interface ParsedPaymentError {
  message: string;
  retryable: boolean;
}

function getErrorMessage(error: any): ParsedPaymentError {
  const responseData = error?.response?.data;

  // Case 1: Structured error from backend (new format with error_type)
  if (responseData?.error_type && responseData?.message) {
    // Use technical_message if available and message is too generic
    const displayMessage = responseData.technical_message || responseData.message;
    return {
      message: displayMessage,
      retryable: responseData.retryable ?? true,
    };
  }

  const errorMessage = responseData?.message ||
                       responseData?.detail ||
                       responseData?.error ||
                       error?.message ||
                       '';

  // Case 2: Legacy bank_decline format: "bank_decline:CODE:message"
  if (typeof errorMessage === 'string' && errorMessage.startsWith('bank_decline:')) {
    const parts = errorMessage.split(':');
    const code = parts[1];
    if (code && BANK_DECLINE_MESSAGES[code]) {
      const info = BANK_DECLINE_MESSAGES[code];
      return { message: info.message, retryable: info.retryable };
    }
    return {
      message: parts.slice(2).join(':') || ERROR_MESSAGES['card_declined'],
      retryable: false,
    };
  }

  // Case 3: Known error codes
  const errorCode = responseData?.error_code || responseData?.code;
  if (errorCode && ERROR_MESSAGES[errorCode]) {
    return { message: ERROR_MESSAGES[errorCode], retryable: true };
  }

  // Case 4: Keyword-based detection (fallback)
  if (errorMessage) {
    if (errorMessage.includes('declined') || errorMessage.includes('rejected') || errorMessage.includes('rechazad')) {
      return { message: ERROR_MESSAGES['card_declined'], retryable: false };
    }
    if (errorMessage.includes('insufficient') || errorMessage.includes('insuficiente')) {
      return { message: ERROR_MESSAGES['insufficient_funds'], retryable: false };
    }
    if (errorMessage.includes('expired') || errorMessage.includes('expirad') || errorMessage.includes('vencid')) {
      return { message: ERROR_MESSAGES['expired_card'], retryable: false };
    }
    if (errorMessage.includes('cvv') || errorMessage.includes('CVV') || errorMessage.includes('security code')) {
      return { message: ERROR_MESSAGES['invalid_cvv'], retryable: true };
    }
    if (errorMessage.includes('subscription') && errorMessage.includes('not found')) {
      return { message: 'No se encontró la suscripción. Por favor, recarga la página e intenta de nuevo.', retryable: false };
    }
    if (errorMessage.includes('plan') && errorMessage.includes('not found')) {
      return { message: 'El plan seleccionado no está disponible. Por favor, selecciona otro plan.', retryable: false };
    }
    if (errorMessage.includes('already exists')) {
      return { message: 'Ya tienes este plan activo. Recarga la página para ver tu suscripción actual.', retryable: false };
    }
    return { message: errorMessage, retryable: true };
  }

  // Network errors
  if (error?.code === 'ERR_NETWORK' || !navigator.onLine) {
    return { message: ERROR_MESSAGES['network_error'], retryable: true };
  }

  return { message: ERROR_MESSAGES['default'], retryable: true };
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: AvailablePlan | null;
  currentSubscriptionId: string | null;
  onSubmit: (cardInfo: CardInfo) => Promise<void>;
  isProcessing: boolean;
}

export function PaymentModal({
  isOpen,
  onClose,
  plan,
  onSubmit,
  isProcessing,
}: PaymentModalProps) {
  const [cardInfo, setCardInfo] = useState<CardInfo>({
    card_number: '',
    exp_month: '',
    exp_year: '',
    cvv: '',
    first_name: '',
    last_name: '',
    card_type: 'VISA',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CardInfo, string>>>({});
  const [serverError, setServerError] = useState<ParsedPaymentError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setCardInfo({
        card_number: '',
        exp_month: '',
        exp_year: '',
        cvv: '',
        first_name: '',
        last_name: '',
        card_type: 'VISA',
      });
      setErrors({});
      setServerError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Limpiar error de campo cuando el usuario modifica el valor
  const clearFieldError = useCallback((field: keyof CardInfo) => {
    setErrors(prev => {
      if (!prev[field]) return prev; // No re-render si no hay error
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
    setServerError(prev => prev ? null : prev); // No re-render si ya es null
  }, []);

  // Track payment modal opened
  useEffect(() => {
    if (isOpen && plan) {
      trackingHelpers.trackPaymentModalOpened({
        planId: plan.id,
        planName: plan.name,
        amount: plan.price_difference,
      });

    }
  }, [isOpen, plan]);

  const validateForm = (): { valid: boolean; errorFields: string[] } => {
    const newErrors: Partial<Record<keyof CardInfo, string>> = {};

    // Validate card number with Luhn algorithm
    const cleanNumber = cardInfo.card_number.replace(/\s/g, '');
    if (!cleanNumber) {
      newErrors.card_number = 'El número de tarjeta es requerido';
    } else if (!validateLuhn(cleanNumber)) {
      newErrors.card_number = 'Número de tarjeta inválido';
    }

    // Validate expiry date
    const expiryResult = validateExpiryDate(cardInfo.exp_month, cardInfo.exp_year);
    if (!cardInfo.exp_month) {
      newErrors.exp_month = 'Mes requerido';
    } else if (!expiryResult.valid && expiryResult.error?.includes('Mes')) {
      newErrors.exp_month = expiryResult.error;
    }

    if (!cardInfo.exp_year) {
      newErrors.exp_year = 'Año requerido';
    } else if (!expiryResult.valid && expiryResult.error && !expiryResult.error.includes('Mes')) {
      newErrors.exp_year = expiryResult.error;
    }

    // Validate CVV based on detected card type
    const currentCardType = detectCardType(cleanNumber);
    const expectedCVVLen = getExpectedCVVLength(currentCardType);
    if (!cardInfo.cvv) {
      newErrors.cvv = 'CVV requerido';
    } else if (cardInfo.cvv.length !== expectedCVVLen) {
      newErrors.cvv = `CVV debe tener ${expectedCVVLen} dígitos`;
    }

    // Validate names
    if (!cardInfo.first_name.trim()) {
      newErrors.first_name = 'El nombre es requerido';
    }

    if (!cardInfo.last_name.trim()) {
      newErrors.last_name = 'El apellido es requerido';
    }

    setErrors(newErrors);
    const errorFields = Object.keys(newErrors);
    return { valid: errorFields.length === 0, errorFields };
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (isSubmitting || isProcessing) return;

    setServerError(null);

    const validation = validateForm();
    if (!validation.valid) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(cardInfo);
    } catch (error: any) {
      console.error('Error processing payment:', error);
      const parsedError = getErrorMessage(error);
      setServerError(parsedError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCardNumberChange = useCallback((value: string) => {
    clearFieldError('card_number');
    const cleaned = value.replace(/\D/g, '');

    // AMEX has 15 digits, others 16
    const detectedType = detectCardType(cleaned);
    const maxLen = detectedType === 'AMEX' ? 15 : 16;
    const limited = cleaned.slice(0, maxLen);

    // Format with spaces (AMEX: 4-6-5, others: 4-4-4-4)
    let formatted: string;
    if (detectedType === 'AMEX') {
      formatted = limited.replace(/(\d{4})(\d{0,6})(\d{0,5})/, (_, a, b, c) =>
        [a, b, c].filter(Boolean).join(' ')
      );
    } else {
      formatted = limited.replace(/(\d{4})/g, '$1 ').trim();
    }

    // Map detected type to card_type field (keep compatible with existing types)
    const cardType = detectedType === 'MASTERCARD' ? 'MASTERCARD'
                   : detectedType === 'AMEX' ? 'AMEX'
                   : 'VISA';

    setCardInfo(prev => ({ ...prev, card_type: cardType, card_number: formatted }));
  }, [clearFieldError]);

  const handleMonthChange = useCallback((value: string) => {
    clearFieldError('exp_month');
    const cleaned = value.replace(/\D/g, '');
    const limited = cleaned.slice(0, 2);
    setCardInfo(prev => ({ ...prev, exp_month: limited }));
  }, [clearFieldError]);

  const handleYearChange = useCallback((value: string) => {
    clearFieldError('exp_year');
    const cleaned = value.replace(/\D/g, '');
    const limited = cleaned.slice(0, 2);
    setCardInfo(prev => ({ ...prev, exp_year: limited }));
  }, [clearFieldError]);

  const handleCvvChange = useCallback((value: string) => {
    clearFieldError('cvv');
    const cleaned = value.replace(/\D/g, '');
    const limited = cleaned.slice(0, 4);
    setCardInfo(prev => ({ ...prev, cvv: limited }));
  }, [clearFieldError]);

  const handleNameChange = useCallback((field: 'first_name' | 'last_name', value: string) => {
    clearFieldError(field);
    setCardInfo(prev => ({ ...prev, [field]: value }));
  }, [clearFieldError]);

  const handleClose = () => {
    onClose();
  };

  if (!plan) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
            <CreditCardIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-secondary-900 dark:text-white">
              Mejorar Plan
            </h2>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              {plan.name}
            </p>
          </div>
        </div>

        <Alert variant="info" className="mb-6">
          <div className="space-y-2">
            <p className="font-semibold">Resumen del cambio:</p>
            <ul className="text-sm space-y-1">
              <li>Plan: {plan.name}</li>
              <li>Precio: ${plan.price.toFixed(2)} {plan.currency}/año</li>
              <li>Diferencia a pagar: ${plan.price_difference.toFixed(2)}</li>
            </ul>
          </div>
        </Alert>

        {/* Error del servidor */}
        {serverError && (
          <Alert variant="danger" className="mb-6">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Error en el pago</p>
                <p className="text-sm mt-1">{serverError.message}</p>
                <p className="text-xs mt-2 opacity-75">
                  {serverError.retryable
                    ? 'Puedes corregir los datos e intentar de nuevo.'
                    : 'Usa otra tarjeta o contacta a tu banco.'}
                </p>
              </div>
            </div>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
              Número de Tarjeta
            </label>
            <Input
              type="text"
              inputMode="numeric"
              autoComplete="cc-number"
              value={cardInfo.card_number}
              onChange={(e) => handleCardNumberChange(e.target.value)}
              placeholder="1234 5678 9012 3456"
              error={errors.card_number}
              disabled={isProcessing || isSubmitting}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Nombre
              </label>
              <Input
                type="text"
                autoComplete="cc-given-name"
                value={cardInfo.first_name}
                onChange={(e) => handleNameChange('first_name', e.target.value)}
                placeholder="Nombre"
                error={errors.first_name}
                maxLength={100}
                disabled={isProcessing || isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Apellido
              </label>
              <Input
                type="text"
                autoComplete="cc-family-name"
                value={cardInfo.last_name}
                onChange={(e) => handleNameChange('last_name', e.target.value)}
                placeholder="Apellido"
                error={errors.last_name}
                maxLength={100}
                disabled={isProcessing || isSubmitting}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Mes
              </label>
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="cc-exp-month"
                value={cardInfo.exp_month}
                onChange={(e) => handleMonthChange(e.target.value)}
                placeholder="MM"
                error={errors.exp_month}
                disabled={isProcessing || isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                Año
              </label>
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="cc-exp-year"
                value={cardInfo.exp_year}
                onChange={(e) => handleYearChange(e.target.value)}
                placeholder="YY"
                error={errors.exp_year}
                disabled={isProcessing || isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                CVV
              </label>
              <Input
                type="password"
                inputMode="numeric"
                autoComplete="cc-csc"
                value={cardInfo.cvv}
                onChange={(e) => handleCvvChange(e.target.value)}
                placeholder="•••"
                error={errors.cvv}
                disabled={isProcessing || isSubmitting}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isProcessing || isSubmitting}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isProcessing || isSubmitting}
              className="flex-1"
            >
              {isProcessing || isSubmitting ? 'Procesando...' : `Pagar $${plan.price_difference.toFixed(2)}`}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
